'use client';

/**
 * TopNav - 站点顶部导航条
 * 仿照 https://ceshi.xiaoquanwk.com/ 顶部（FastAdmin index.html 的 <nav class="nav">）
 * - 仅在 dongniai 路由下渲染（由 app/layout.tsx 引入）
 * - 玻璃态背景 + 滚动阴影
 * - 登录态同步 FastAdmin 会话（useAuthStore），与课堂页 Header 行为一致
 *
 * ⚠️ 与课堂页（classroom/[id]）的 <Header /> 组件的关系：
 *   - TopNav 在最外层，所有 dongniai 页面共用
 *   - 课堂页内部 <Header /> 显示当前场景名 + 导出按钮，属于"页面级工具栏"
 *   - 两层并存：TopNav 负责"跨页导航"，课堂 Header 负责"课堂操作"
 *
 * 边界：
 *   - 路径匹配规则：usePathname 在 /classroom/[id] 时返回 "/classroom/xxx"，
 *     此时 nav-links 里"首页"/"价格"等不会高亮（视为非首页场景）
 *   - "下载"按钮：客户端未上线，沿用 FastAdmin 模板的 alert 占位
 */

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { GraduationCap, BookOpenCheck } from 'lucide-react';
import { useAuthStore } from '@/lib/store/auth';
import { cn } from '@/lib/utils';
import NoticeBell from '@/components/notice-bell';

interface NavLink {
  label: string;
  href: string;
  /** 是否外部链接（用 <a> 而非 next/link，但本项目全部为站内 URL，统一用 <a>） */
  external?: boolean;
  /** 匹配高亮用的前缀（默认等于 href） */
  matchPrefix?: string;
  /** 自定义 onClick（用于"下载"这种 alert 占位） */
  onClick?: () => void;
}

export function TopNav() {
  const pathname = usePathname();
  const { loggedIn, nickname, checked } = useAuthStore();
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  // 2026-07-14 P1 fix: dongniai 子页面(/u/*, /classroom/* 等)之前没调 fetchStatus,
  // 导致 TopNav 一直显示「登录/注册」, 即使用户其实已经登录
  // (cookie httpOnly, fetch 默认带, API 返回 loggedIn:true)
  // 之前只有 /  (app/page.tsx 649 行) 调用了 fetchStatus, 这次补全所有用 TopNav 的页面
  // (首页已经调过了, 这里多调一次无害 — fetchStatus 是 idempotent, 只更新 store)
  useEffect(() => {
    useAuthStore.getState().fetchStatus();
  }, []);

  // 滚动时加阴影
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // 避免 hydration mismatch：登录态在 SSR 时是 false，客户端 hydrate 后才显示真实状态
  useEffect(() => {
    setMounted(true);
  }, []);

  // 5 个主按钮（"懂你AI课堂"作为 logo，不算 nav-link）
  // 2026-07-30 老板 P0：所有"立即上课"按钮右侧加"习题演练"按钮（青绿渐变次级 CTA，与主 CTA 颜色区分）
  // 【v6.97.140+31 P0 修复 2026-09-17 老板报】"首页" / "价格" 按钮跳错目标
  //   - 修前:
  //     - 首页 → '/dongniai/' (修后还是错的, 用户期望跳站根 https://www.xiaoquanwk.com/ 即 FastAdmin 营销首页, 跟 help/index.html 同款)
  //     - 价格 → '/KoOIsYnLPb.php/index/addons/exec?action=recharge&ref=addtabs' (回后台 recharge 充值页, 不是营销"价格"页)
  //   - 修后 (跟 /www/wwwroot/ceshi.xiaoquanwk.com/public/help/index.html 第 116/119 行 nav-links 完全对齐):
  //     - 首页 → '/'  (站根 FastAdmin 营销首页, 整页刷新)
  //     - 价格 → '/price'  (nginx 配 location = /price → price.php 营销价格页, HTTP=200 已 curl 验证)
  //   - 之前 'href:/dongniai/' 相对路径的 basePath 重写坑 跟下面"立即上课"/"习题演练" 一样 (它们仍是 /dongniai/xxx 走 SPA 内), 这里
  //     '首页' 跳站根是预期行为 (跨 SPA 边界), '价格' /price nginx 路由命中不走 SPA → 都用站相对路径即可 (浏览器以当前 URL 为基址解析, /price 永远解析为站根 + /price)
  const links: NavLink[] = [
    { label: '首页', href: '/' },
    { label: '价格', href: '/price' },
    {
      label: '下载',
      href: '#',
      onClick: () => alert('客户端正在开发中，敬请期待'),
    },
    {
      label: '立即上课',
      href: '/dongniai/',
      matchPrefix: '/dongniai',
    },
    // 【v6.97.119 P0 修复 2026-09-12 老板报】"习题演练"/"使用说明" 链接 href 改绝对路径
    //   修复前: '/exercise/' 相对路径在 /dongniai/ basePath 下, 浏览器请求 /exercise/ 走 nginx → fastadmin 兜底 → /index/user/login.html 或 /dashboard?ref=addtabs
    //   修复后:
    //     - "习题演练" 跳 /dongniai/exercise/ (Next.js 路由, 跟 v6.97.83+5 模板统一绝对路径)
    //     - "使用说明" 跳 /help/index.html (nginx 静态页, 站外, 需用 window.location.origin 防 basePath 重写)
    {
      label: '习题演练',
      href: '/dongniai/exercise/',
      matchPrefix: '/dongniai/exercise',
    },
    {
      label: '使用说明',
      href: typeof window !== 'undefined' ? window.location.origin + '/help/index.html' : '/help/index.html',
      external: true,
    },
  ];

  // 高亮判断：当前 pathname 是否以 matchPrefix 开头
  const isActive = (link: NavLink): boolean => {
    // usePathname() 返回类型是 string | null，保守处理
    if (!pathname) return false;
    const prefix = link.matchPrefix ?? link.href;
    // 外部链接的"首页"/"价格"：永远不高亮（因为我们不在那个站点）
    if (link.href.startsWith('http')) return false;
    return pathname === prefix || pathname.startsWith(prefix + '/');
  };

  return (
    <nav
      className={cn(
        // 用 sticky 而非 fixed：让 nav 在文档流中"占据" 68px 高度，
        // 避免被 fixed 遮挡时还要手动给各页面加 padding-top
        'sticky top-0 z-[1000]',
        'bg-white/[0.88] dark:bg-gray-900/[0.88]',
        'backdrop-blur-[20px] backdrop-saturate-[180%]',
        'border-b border-gray-200/60 dark:border-gray-700/60',
        'transition-[background,box-shadow] duration-300',
        scrolled
          ? 'bg-white/[0.96] dark:bg-gray-900/[0.96] shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
          : '',
      )}
      suppressHydrationWarning
    >
      <div className="max-w-[1200px] mx-auto px-8 flex items-center h-[68px]">
        {/* Logo */}
        <a
          href="/"
          className="text-[20px] font-black text-[#4F46E5] tracking-[-0.5px] mr-10 flex items-center gap-2"
        >
          懂你<span className="text-[#1E293B] dark:text-gray-100 font-bold">AI课堂</span>
        </a>

        {/* 中间链接 */}
        <ul className="flex gap-2 list-none flex-1">
          {links.map((link) => {
            const active = isActive(link);
            return (
              <li key={link.label}>
                <a
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={(e) => {
                    if (link.onClick) {
                      e.preventDefault();
                      link.onClick();
                    }
                  }}
                  className={cn(
                    'text-[14px] font-medium px-4 py-2 rounded-lg transition-all duration-200 inline-flex items-center gap-1.5',
                    active
                      ? 'text-[#4F46E5] bg-[#4F46E5]/[0.06]'
                      : 'text-[#475569] dark:text-gray-300 hover:text-[#4F46E5] hover:bg-[#4F46E5]/[0.06]',
                    // "立即上课" 按钮特殊样式：蓝紫渐变填充（主 CTA，进课堂学习）
                    link.label === '立即上课' &&
                      'bg-gradient-to-br from-[#4F46E5] to-[#6366F1] text-white hover:text-white shadow-[0_4px_14px_rgba(79,70,229,0.35)] hover:shadow-[0_8px_25px_rgba(79,70,229,0.4)] hover:-translate-y-0.5',
                    link.label === '立即上课' && !active && 'hover:bg-gradient-to-br',
                    // 2026-07-30 老板 P0：所有"立即上课"右侧加"习题演练"按钮（青绿渐变次级 CTA，与主 CTA 颜色区分）
                    link.label === '习题演练' &&
                      'bg-gradient-to-br from-[#10B981] to-[#14B8A6] text-white hover:text-white shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:shadow-[0_8px_25px_rgba(16,185,129,0.4)] hover:-translate-y-0.5',
                    link.label === '习题演练' && !active && 'hover:bg-gradient-to-br',
                    // "使用说明" 按钮特殊样式：Ghost 透明带边框，外发链接
                    link.label === '使用说明' && 'border border-[#4F46E5]/20 dark:border-white/10 bg-[#4F46E5]/[0.04] dark:bg-white/[0.04] hover:bg-[#4F46E5]/[0.1] dark:hover:bg-white/[0.08] hover:border-[#4F46E5]/35 dark:hover:border-white/25',
                  )}
                >
                  {/* 2026-07-30 老板 P0：大厂标准 — 主次按钮分别带 icon，视觉区分 + 增强辨识 */}
                  {link.label === '立即上课' && <GraduationCap className="w-[15px] h-[15px]" strokeWidth={2.5} />}
                  {link.label === '习题演练' && <BookOpenCheck className="w-[15px] h-[15px]" strokeWidth={2.5} />}
                  {link.label}
                  {link.external && <span className="ml-1 text-[11px] opacity-60">↗</span>}
                </a>
              </li>
            );
          })}
        </ul>

        {/* 右侧登录/用户区 */}
        <div className="flex items-center gap-2">
          {mounted && checked && loggedIn ? (
            <>
              <NoticeBell />
              {/* 【v6.97.85+11 P0 修复 2026-09-02 老板报】同 v6.97.83+5 模板, 用户中心/退出 链接 href 改绝对路径
                  防 SSR 渲染相对路径 + 防右键复制 + 防 Nginx @fastadmin 兜底重写 → /dashboard?ref=addtabs */}
              <a
                href={typeof window !== 'undefined'
                  ? window.location.origin + '/index/user/index.html'
                  : '/index/user/index.html'}
                className="inline-flex items-center justify-center gap-2 px-[22px] py-[10px] rounded-full text-[14px] font-semibold bg-transparent text-[#475569] dark:text-gray-300 hover:bg-[#F4F5FA] dark:hover:bg-gray-800 hover:text-[#1E293B] dark:hover:text-gray-100 transition-all whitespace-nowrap"
              >
                <span aria-hidden>👤</span>
                {nickname}
              </a>
              <a
                href={typeof window !== 'undefined'
                  ? window.location.origin + '/index/user/logout.html?url=/dongniai/'
                  : '/index/user/logout.html?url=/dongniai/'}
                className="inline-flex items-center justify-center px-[22px] py-[10px] rounded-full text-[14px] font-semibold bg-transparent text-[#475569] dark:text-gray-300 hover:bg-[#F4F5FA] dark:hover:bg-gray-800 hover:text-[#1E293B] dark:hover:text-gray-100 transition-all whitespace-nowrap"
              >
                退出
              </a>
            </>
          ) : (
            <>
              {/* 【v6.97.83+5 P0 修复 2026-09-01 老板报】a 标签 href 改用绝对路径, 绕开 basePath 重写 */}
              <a
                href={typeof window !== 'undefined'
                  ? window.location.origin + '/index/user/login.html?url=/dongniai/'
                  : '/index/user/login.html?url=/dongniai/'}
                className="inline-flex items-center justify-center px-[22px] py-[10px] rounded-full text-[14px] font-semibold bg-transparent text-[#475569] dark:text-gray-300 hover:bg-[#F4F5FA] dark:hover:bg-gray-800 hover:text-[#1E293B] dark:hover:text-gray-100 transition-all whitespace-nowrap"
              >
                登录
              </a>
              {/* 【v6.97.83+5 P0 修复 2026-09-01 老板报】同 login, 注册链接也升级绝对路径 */}
              <a
                href={typeof window !== 'undefined'
                  ? window.location.origin + '/index/user/register.html?url=/dongniai/'
                  : '/index/user/register.html?url=/dongniai/'}
                className="inline-flex items-center justify-center px-[22px] py-[10px] rounded-full text-[14px] font-semibold bg-gradient-to-br from-[#4F46E5] to-[#6366F1] text-white shadow-[0_4px_14px_rgba(79,70,229,0.35)] hover:shadow-[0_8px_25px_rgba(79,70,229,0.4)] hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                注册
              </a>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
